import { useState, useEffect, useCallback } from 'react'
import { Outlet, NavLink, useSearchParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  BarChart3, PieChart, Users, User, Tags, Calendar, DollarSign, Hourglass,
  SlidersHorizontal, ChevronDown,
} from 'lucide-react'
import DateFilterBar, { getDateRangeForMode, type OutputMode } from '../../components/DateFilterBar'
import KlasseFilterPopover from '../../components/KlasseFilterPopover'
import PostnummerFilter, { type PostnummerDirection } from '../../components/PostnummerFilter'
import { useMaxDataDate } from '../../hooks/useMaxDataDate'
import { useActiveMarkedsomrade } from '../../hooks/useActiveMarkedsomrade'
import { useBruktbilFilters } from './useBruktbilFilters'
import { filterSummary } from './filterSummary'
import type { BruktbilOutletContext } from './FilterSlot'
import { useBruktbilAvansert } from './tilgang'

interface GlobalDraft {
  klasse: string[]
  omregistrering: boolean
  postnummerEnabled: boolean
  postnummerDirection: PostnummerDirection
  markedsomradeId: number | null
}

// What Nullstill restores — the module defaults, not an empty filter set.
const DEFAULT_GLOBAL_DRAFT: GlobalDraft = {
  klasse: [],
  omregistrering: false,
  postnummerEnabled: false,
  postnummerDirection: 'begge',
  markedsomradeId: null,
}

// `avansert` tabs need usedcartransfer_advanced (#673).
const TABS = [
  { labelKey: 'layout.tabs.oversikt', path: 'oversikt', icon: BarChart3, avansert: false },
  { labelKey: 'layout.tabs.klasser', path: 'klasser', icon: PieChart, avansert: false },
  { labelKey: 'layout.tabs.selgere', path: 'selgere', icon: Users, avansert: true },
  { labelKey: 'layout.tabs.kjopere', path: 'kjopere', icon: User, avansert: true },
  { labelKey: 'layout.tabs.merkeModell', path: 'merke-modell', icon: Tags, avansert: false },
  { labelKey: 'layout.tabs.aldersprofil', path: 'aldersprofil', icon: Calendar, avansert: false },
  { labelKey: 'layout.tabs.priser', path: 'priser', icon: DollarSign, avansert: true },
  { labelKey: 'layout.tabs.staatid', path: 'staatid', icon: Hourglass, avansert: false },
] as const

// Params owned by the shared filter bar — carried across tab switches.
// Page-local state (tab, merke, modell, aarMin, aarMax) must not leak between tabs.
const SHARED_PARAMS = ['from', 'to', 'klasse', 'omregistrering', 'geo', 'geoDir'] as const

export default function BruktbilLayout() {
  const { t } = useTranslation('bruktbil')
  const maxDataDate = useMaxDataDate()
  const avansert = useBruktbilAvansert()
  const tabs = TABS.filter(tab => avansert || !tab.avansert)
  const location = useLocation()
  // Ståtid has its own filter set (backend #274) and the shared klasse /
  // varebil / omregistrering filters do NOT reach its endpoints — StaatidFilters
  // has no such fields. Rendering them there gave the tab three controls that
  // silently did nothing to the numbers (#523). Same reasoning as the global
  // vehicle toggle in Header.tsx, which is hidden for the same route.
  const onStaatid = location.pathname.startsWith('/bruktbil/staatid')
  const {
    from, to, klasse, omregistrering, postnummerEnabled, postnummerDirection,
    postnummerNavn, setFilters,
  } = useBruktbilFilters()
  const { activeId, setActiveId, clearActive } = useActiveMarkedsomrade()
  const [outputMode, setOutputMode] = useState<OutputMode>('year_to_date')
  const [filterSlot, setFilterSlot] = useState<HTMLDivElement | null>(null)
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(true)

  // #526 — every filter on screen is a DRAFT until the user clicks Oppdater.
  // Previously klasse / eierskifte / postnummer wrote straight to the URL, so
  // each click fired a fresh request against the heaviest endpoints, often with
  // a half-configured filter set. Only the period was ever commit-based.
  const applied: GlobalDraft = {
    klasse, omregistrering, postnummerEnabled, postnummerDirection, markedsomradeId: activeId,
  }
  const [draft, setDraft] = useState<GlobalDraft>(applied)

  // Tab-local filters (classification, merke, …) join the same commit point via
  // useLocalFilter — the layout holds their draft and applied values so one
  // Oppdater covers the whole box. Cleared on tab change: page-local filter
  // state must not leak between tabs, same rule as SHARED_PARAMS above.
  const [localDefaults, setLocalDefaults] = useState<Record<string, unknown>>({})
  const [localDraft, setLocalDraftState] = useState<Record<string, unknown>>({})
  const [localApplied, setLocalApplied] = useState<Record<string, unknown>>({})

  const registerLocalFilter = useCallback((key: string, defaultValue: unknown) => {
    setLocalDefaults(d => (key in d ? d : { ...d, [key]: defaultValue }))
    setLocalDraftState(d => (key in d ? d : { ...d, [key]: defaultValue }))
    setLocalApplied(d => (key in d ? d : { ...d, [key]: defaultValue }))
  }, [])

  const setLocalDraft = useCallback((key: string, value: unknown) => {
    setLocalDraftState(d => ({ ...d, [key]: value }))
  }, [])

  const tabPath = location.pathname
  useEffect(() => {
    setLocalDefaults({})
    setLocalDraftState({})
    setLocalApplied({})
  }, [tabPath])

  // Re-sync the draft when the applied filters change from outside this box —
  // a tab link carrying different params, or the back button.
  const [searchParams] = useSearchParams()
  const appliedKey = JSON.stringify(applied)
  useEffect(() => {
    setDraft(applied)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey])

  function rangeFor(mode: OutputMode) {
    if (mode === 'custom') return { from: customStart, to: customEnd }
    const range = getDateRangeForMode(
      mode,
      maxDataDate ? new Date(maxDataDate + 'T12:00:00') : null,
    )
    return { from: range.startDate, to: range.endDate }
  }

  function currentRange() {
    return rangeFor(outputMode)
  }

  // The period is a staged filter like any other, but its draft lives in
  // DateFilterBar state (outputMode + custom dates), not in `draft` — so the
  // dirty check has to derive it. The applied side is from/to in the URL;
  // when the URL carries none, the applied period IS the default mode, and it
  // has to be compared through the same maxDataDate cap as the draft or the
  // box would wake up dirty while the cutoff loads.
  const customIncomplete = outputMode === 'custom' && (!customStart || !customEnd)
  const periodExplicit = searchParams.has('from') || searchParams.has('to')
  const appliedRange = periodExplicit ? { from, to } : rangeFor('year_to_date')
  const draftRange = currentRange()
  const periodDirty =
    !customIncomplete &&
    (draftRange.from !== appliedRange.from || draftRange.to !== appliedRange.to)

  const isDirty =
    periodDirty ||
    appliedKey !== JSON.stringify(draft) ||
    JSON.stringify(localApplied) !== JSON.stringify(localDraft)

  // An incomplete custom period can never be committed — applying would write
  // an empty from/to. Same guard DateFilterBar's own button had (customReady).
  const canApply = isDirty && !customIncomplete

  // The collapsed line describes what the numbers on screen were built from,
  // so it reads `applied`/`localApplied` — never the draft.
  const summary = filterSummary({
    from, to, klasse, omregistrering, postnummerEnabled, postnummerNavn,
    showKlasseOgEierskifte: !onStaatid,
    tabFiltersActive: JSON.stringify(localApplied) !== JSON.stringify(localDefaults),
  })

  /** The single commit point: one request, with every filter applied. */
  function handleApply() {
    if (!canApply) return
    const { from, to } = currentRange()
    // localStorage-backed and read by useMinePostnummer, so it has to be
    // committed here rather than when the dropdown changes.
    if (draft.markedsomradeId === null) clearActive()
    else if (draft.markedsomradeId !== activeId) setActiveId(draft.markedsomradeId)
    setLocalApplied(localDraft)
    setFilters({
      from,
      to,
      klasse: draft.klasse,
      omregistrering: draft.omregistrering,
      postnummerEnabled: draft.postnummerEnabled,
      postnummerDirection: draft.postnummerDirection,
    })
    // Applying is the end of the filtering task — fold the box back up and
    // give the viewport to the results the user just asked for.
    setFiltersOpen(false)
  }

  /** Back to the module defaults — and NOT a commit: the user confirms with
   * Oppdater, so Nullstill is not an exception to the rule above (#526 AC3). */
  function handleReset() {
    setOutputMode('year_to_date')
    setCustomStart('')
    setCustomEnd('')
    setDraft(DEFAULT_GLOBAL_DRAFT)
    setLocalDraftState(localDefaults)
  }

  const shared = new URLSearchParams()
  for (const key of SHARED_PARAMS) {
    for (const value of searchParams.getAll(key)) shared.append(key, value)
  }
  const tabSearch = shared.toString()

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('layout.title')}</h1>

      {/* Tab bar — underline style, same look as LeadsPage */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 gap-1 overflow-x-auto">
        {tabs.map(tab => (
          <NavLink
            key={tab.path}
            to={{ pathname: `/bruktbil/${tab.path}`, search: tabSearch }}
            className={({ isActive }) =>
              `px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
                isActive
                  ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`
            }
          >
            <span className="flex items-center gap-2">
              <tab.icon className="w-4 h-4" />
              {t(tab.labelKey)}
            </span>
          </NavLink>
        ))}
      </div>

      {/* One box, one commit point (#526). Every control below writes to a
          draft; nothing reaches the API until Oppdater.

          Expanded by default (#586). It shipped collapsed because open it ran
          to roughly half the viewport, pushing the page's sub-tabs so far down
          that the two tab levels stopped reading as a hierarchy. But that put
          every filter behind a click for users who arrive wanting to change
          one, which is most of them. Open is the better default; the
          disclosure stays, so anyone who wants the viewport back can fold it,
          and Oppdater still folds it on apply. Collapsed, the one line states
          the whole applied filter set — nothing is hidden, only folded. */}
      <div className="card p-3 space-y-2" data-testid="klasse-filter-box">
        <button
          type="button"
          onClick={() => setFiltersOpen(o => !o)}
          aria-expanded={filtersOpen}
          aria-controls="bruktbil-filter-panel"
          data-testid="bruktbil-filter-toggle"
          className="w-full flex items-center justify-between gap-3 text-left"
        >
          <span className="flex items-center gap-2 min-w-0">
            <SlidersHorizontal className="w-4 h-4 shrink-0 text-gray-400 dark:text-gray-500" />
            <span
              data-testid="bruktbil-filter-summary"
              className="text-sm text-gray-700 dark:text-gray-300 truncate"
            >
              {summary.join(' · ')}
            </span>
          </span>
          <span className="flex items-center gap-1 shrink-0 text-xs font-medium text-primary-600 dark:text-primary-400">
            {filtersOpen ? t('layout.hideFilters') : t('layout.showFilters')}
            <ChevronDown
              className={`w-4 h-4 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
            />
          </span>
        </button>

        <div
          id="bruktbil-filter-panel"
          hidden={!filtersOpen}
          className="space-y-2 pt-2 border-t border-gray-200 dark:border-dark-border"
        >
        <DateFilterBar
          outputMode={outputMode}
          onOutputModeChange={setOutputMode}
          customStartDate={customStart}
          onCustomStartDateChange={setCustomStart}
          customEndDate={customEnd}
          onCustomEndDateChange={setCustomEnd}
          onApply={handleApply}
          hideApply
          maxDataDate={maxDataDate}
        />
        {!onStaatid && (
          <>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              {t('layout.vehicleTypeHint')}
            </p>
            <div className="flex items-center gap-3 mt-3" data-testid="eierskifte-filter">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('layout.eierskifte')}</span>
              {/* Tonal, not solid — see FilterChip: a filter must not carry the
                  same weight as the Oppdater button that applies it. */}
              <div className="flex rounded-sm border border-gray-300 dark:border-gray-600 overflow-hidden text-xs">
                <button
                  onClick={() => setDraft(d => ({ ...d, omregistrering: false }))}
                  aria-pressed={!draft.omregistrering}
                  className={`px-3 py-1 transition-colors ${
                    !draft.omregistrering
                      ? 'bg-primary-50 text-primary-700 font-medium dark:bg-primary-900/40 dark:text-primary-200'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {t('layout.alle')}
                </button>
                <button
                  onClick={() => setDraft(d => ({ ...d, omregistrering: true }))}
                  aria-pressed={draft.omregistrering}
                  className={`px-3 py-1 border-l border-gray-300 dark:border-gray-600 transition-colors ${
                    draft.omregistrering
                      ? 'bg-primary-50 text-primary-700 font-medium dark:bg-primary-900/40 dark:text-primary-200'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {t('layout.kunOmregistrering')}
                </button>
              </div>
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('layout.klasse')}</span>
            <KlasseFilterPopover
              selected={draft.klasse}
              onChange={k => setDraft(d => ({ ...d, klasse: k }))}
            />
          </>
        )}

        {/* Tab-local filters (#483) portal in here — see FilterSlot. They bind
            to the same draft via useLocalFilter, so this button covers them. */}
        <div ref={setFilterSlot} className="empty:hidden pt-1" data-testid="bruktbil-filter-slot" />

        <PostnummerFilter
          compact
          enabled={draft.postnummerEnabled}
          onToggle={v => setDraft(d => ({ ...d, postnummerEnabled: v }))}
          direction={draft.postnummerDirection}
          onDirectionChange={dir => setDraft(d => ({ ...d, postnummerDirection: dir }))}
          omradeId={draft.markedsomradeId}
          onOmradeChange={id => setDraft(d => ({ ...d, markedsomradeId: id }))}
        />

        <div className="flex items-center gap-3 pt-2 border-t border-gray-200 dark:border-dark-border">
          {/* The one action in this box, and it has to look like it. It used to
              be btn-primary at 40% opacity whenever nothing was pending — which
              is the box's resting state — so the page's primary action spent
              most of its life fainter than the chips feeding it. Now: full
              cyan the moment there is something to apply, and a plainly inert
              control rather than a washed-out primary when there isn't. */}
          <button
            onClick={handleApply}
            disabled={!canApply}
            data-testid="bruktbil-oppdater"
            className={`text-sm font-semibold px-4 py-2 rounded-lg transition-colors ${
              canApply
                ? 'bg-primary-600 text-white shadow-xs hover:bg-primary-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-dark-card dark:text-gray-500'
            }`}
          >
            {t('layout.oppdater')}
          </button>
          <button
            onClick={handleReset}
            data-testid="bruktbil-nullstill"
            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
          >
            {t('layout.nullstill')}
          </button>
          {isDirty && (
            <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="bruktbil-dirty-hint">
              {t('layout.dirtyHint')}
            </span>
          )}
        </div>
        </div>
      </div>

      <Outlet
        context={{
          filterSlot, localDraft, localApplied, setLocalDraft, registerLocalFilter,
        } satisfies BruktbilOutletContext}
      />
    </div>
  )
}
